import { z } from 'zod';
import { INFERRED_TYPES, type InferredType } from './uploader';

/**
 * The Portfolio Segment: the loan-portfolio slice a user is building. Versioned from
 * day one so the deferred set (per-period custom slicing, named saved segments, AI
 * ranking, segment analytics) is purely additive. The logic is **AND across fields,
 * OR within a field**: a loan matches the segment when it satisfies EVERY field
 * filter, and satisfies a field filter when it matches ANY of that filter's clauses.
 *
 * Shared by the server WHERE-builder (segment-query.ts), the persisted client store,
 * and the Segment Builder UI so the count, the loan list, and the controls can never
 * disagree about what a segment means.
 */
export const SEGMENT_VERSION = 1;

/** One numeric/date range clause. Open-ended when a bound is null (≥min / ≤max). For
 *  date fields the bounds are YYYYMMDD integer ordinals (the projection's date axis). */
export const SegmentRangeSchema = z.object({
  min: z.number().nullable(),
  max: z.number().nullable(),
});
export type SegmentRange = z.infer<typeof SegmentRangeSchema>;

/** Text operators. `is_blank`/`is_not_blank` ignore `value`. */
export const SEGMENT_TEXT_OPS = [
  'contains',
  'equals',
  'starts_with',
  'ends_with',
  'not_contains',
  'regex',
  'is_blank',
  'is_not_blank',
] as const;
export const SegmentTextOpSchema = z.enum(SEGMENT_TEXT_OPS);
export type SegmentTextOp = (typeof SEGMENT_TEXT_OPS)[number];

/** Longest regex we will compile server-side (a cheap DoS guard alongside the
 *  per-query statement_timeout). */
export const SEGMENT_REGEX_MAX_LENGTH = 200;

const NumericFilterSchema = z.object({
  key: z.string(),
  valueType: z.literal('numeric'),
  /** OR of ranges — multiple ranges carve out exclusions (e.g. omit 2008 & COVID). */
  ranges: z.array(SegmentRangeSchema),
  /** Also match loans with NO usable numeric value for this field. */
  includeMissing: z.boolean(),
});

const DateFilterSchema = z.object({
  key: z.string(),
  valueType: z.literal('date'),
  ranges: z.array(SegmentRangeSchema),
  includeMissing: z.boolean(),
});

const CategoricalFilterSchema = z.object({
  key: z.string(),
  valueType: z.literal('categorical'),
  /** `include` → keep loans whose value is in `values`; `exclude` → drop them. */
  mode: z.enum(['include', 'exclude']),
  values: z.array(z.string()),
  /** Treat a blank/missing value as a member of `values` for the mode above. */
  includeBlank: z.boolean(),
});

const TextFilterSchema = z.object({
  key: z.string(),
  valueType: z.literal('text'),
  op: SegmentTextOpSchema,
  value: z.string(),
  caseSensitive: z.boolean(),
});

export const FieldFilterSchema = z.discriminatedUnion('valueType', [
  NumericFilterSchema,
  DateFilterSchema,
  CategoricalFilterSchema,
  TextFilterSchema,
]);
export type FieldFilter = z.infer<typeof FieldFilterSchema>;
export type NumericFieldFilter = z.infer<typeof NumericFilterSchema>;
export type DateFieldFilter = z.infer<typeof DateFilterSchema>;
export type CategoricalFieldFilter = z.infer<typeof CategoricalFilterSchema>;
export type TextFieldFilter = z.infer<typeof TextFilterSchema>;

/** The readiness/transaction toggles (the existing Portfolio boolean filters), folded
 *  into the segment so one object drives the whole view. */
export const SegmentReadinessSchema = z.object({
  creditLoss: z.boolean().optional(),
  payoff: z.boolean().optional(),
  prepayment: z.boolean().optional(),
  hasTransactions: z.boolean().optional(),
});
export type SegmentReadiness = z.infer<typeof SegmentReadinessSchema>;

export const SegmentSchema = z.object({
  version: z.literal(SEGMENT_VERSION),
  fields: z.array(FieldFilterSchema),
  readiness: SegmentReadinessSchema,
});
export type Segment = z.infer<typeof SegmentSchema>;

export const EMPTY_SEGMENT: Segment = { version: SEGMENT_VERSION, fields: [], readiness: {} };

function asInferredType(value: string): InferredType | null {
  return (INFERRED_TYPES as readonly string[]).includes(value) ? (value as InferredType) : null;
}

/** Does a field filter impose any actual constraint? An empty numeric/date filter (no
 *  ranges, not includeMissing) or empty categorical (no values, not includeBlank) is a
 *  no-op and is dropped so it never widens or narrows — and never hits the DB. */
export function isEffectiveFilter(filter: FieldFilter): boolean {
  switch (filter.valueType) {
    case 'numeric':
    case 'date':
      return (
        filter.includeMissing ||
        filter.ranges.some((range) => range.min !== null || range.max !== null)
      );
    case 'categorical':
      return filter.values.length > 0 || filter.includeBlank;
    case 'text':
      if (filter.op === 'is_blank' || filter.op === 'is_not_blank') return true;
      return filter.value.trim().length > 0;
  }
}

/**
 * Reconcile a segment against the org's CURRENT active field types (key → valueType):
 * drop filters whose field is gone or whose stored valueType no longer matches the
 * registry (graceful degrade after a re-type/exclude), and drop no-op filters. Pure;
 * run on hydrate (client) and defensively before querying (server) so a stale stored
 * segment can never throw or mis-slice.
 */
export function normalizeSegment(
  segment: Segment,
  activeFieldTypes: ReadonlyMap<string, string>,
): Segment {
  const fields = segment.fields
    .filter((filter) => {
      if (!isEffectiveFilter(filter)) return false;
      const registered = activeFieldTypes.get(filter.key);
      return registered !== undefined && asInferredType(registered) === filter.valueType;
    })
    // Categorical filtering is inclusion-only in the UI (the Include/Exclude toggle
    // was removed). Pre-launch there is no production data, but an old localStorage
    // draft could still carry mode:'exclude' — coerce it to 'include' on hydrate so
    // it doesn't behave surprisingly against the inclusion-only checklist.
    .map((filter) =>
      filter.valueType === 'categorical' && filter.mode === 'exclude'
        ? { ...filter, mode: 'include' as const }
        : filter,
    );
  return { version: SEGMENT_VERSION, fields, readiness: { ...segment.readiness } };
}

/** True when the segment imposes no constraint at all (whole portfolio). */
export function isEmptySegment(segment: Segment): boolean {
  return (
    segment.fields.every((filter) => !isEffectiveFilter(filter)) &&
    !segment.readiness.creditLoss &&
    !segment.readiness.payoff &&
    !segment.readiness.prepayment &&
    !segment.readiness.hasTransactions
  );
}

/**
 * F100 — constructs that JavaScript's `RegExp` accepts but Postgres POSIX ARE (the engine
 * the `~`/`~*` operators actually run) does NOT. Segment regex filters are executed by
 * Postgres, so validating them with the JS engine is the wrong dialect: a JS-valid /
 * PG-invalid pattern used to pass client + server validation and then 500 at SQL. Reject
 * these at build time so validation matches execution:
 *  - **named capture groups** `(?<name>…)` — no ARE equivalent. (Distinguished from the
 *    lookbehind constraints `(?<=…)` / `(?<!…)`, which ARE does support and we allow.)
 *  - **named backreferences** `\k<name>` — no ARE equivalent.
 *  - **Unicode property escapes** `\p{…}` / `\P{…}` — no ARE equivalent.
 * A rarer PG-invalid pattern that slips past this cheap check is still caught as a clean 422
 * by the SQLSTATE-2201B mapping at execution (segment-query `withSegmentStatementTimeout`),
 * so the two engines can never disagree in a way that silently widens the population.
 */
export function isPosixAreUnsupportedRegex(pattern: string): boolean {
  return (
    /\(\?<[^=!]/.test(pattern) || // named group (?<name> — NOT lookbehind (?<= / (?<!
    /\\k</.test(pattern) || // named backreference \k<name>
    /\\[pP]\{/.test(pattern) // Unicode property escape \p{…} / \P{…}
  );
}

/**
 * Compile a user regex under the safety cap, or null if it is empty, too long, JS-invalid,
 * OR outside the Postgres POSIX-ARE subset ({@link isPosixAreUnsupportedRegex}) — i.e. any
 * pattern Postgres could not run. The returned `RegExp` is only used for build-time preview /
 * tests; the authoritative match runs in Postgres. Callers MUST treat null as "invalid — reject"
 * and never as "no filter" (F100: an invalid regex must never silently widen the slice).
 */
export function safeCompileRegex(pattern: string, caseSensitive: boolean): RegExp | null {
  if (pattern.length === 0 || pattern.length > SEGMENT_REGEX_MAX_LENGTH) return null;
  if (isPosixAreUnsupportedRegex(pattern)) return null;
  try {
    return new RegExp(pattern, caseSensitive ? '' : 'i');
  } catch {
    return null;
  }
}
